import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiDownload, FiRotateCw } from "react-icons/fi";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { Image } from "../../components/Image";
import { Modal } from "../../components/Modal";
import { Paragraph } from "../../components/Paragraph";

export type ViewableImage = {
  key: string;
  label: string;
  base64?: string;
  downloadName: string;
};

export const downloadBase64Image = (base64: string, fileName: string) => {
  const link = document.createElement("a");
  link.href = `data:image/jpg;base64,${base64}`;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/** Rotation per image, shared by a thumbnail and its full-size view. */
export const useImageRotations = () => {
  const [rotations, setRotations] = useState<Record<string, number>>({});
  const rotate = (key: string) =>
    setRotations((prev) => ({ ...prev, [key]: ((prev[key] ?? 0) + 90) % 360 }));
  return { rotationOf: (key: string) => rotations[key] ?? 0, rotate };
};

/** Small captures are scaled up to fill the space, but not beyond this. */
const MAX_UPSCALE = 3;

const FitBox = styled.div<{ $centre?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: ${({ $centre }) => ($centre ? "center" : "flex-start")};
`;

/** Sized to the image as it appears, so nothing around it is left empty. */
const Stage = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const imageFrame = ({ theme: { colors, radii } }) => `
  display: block;
  box-sizing: border-box;
  border: 1px solid ${colors.gray[100]};
  border-radius: ${radii[0]};
`;

const StagedImage = styled.img`
  ${imageFrame}
  position: absolute;
  top: 50%;
  left: 50%;
  max-width: none;
  transition: transform 0.3s;
`;

const LoadingImage = styled(Image)`
  ${imageFrame}
  max-width: 100%;
`;

/**
 * An image scaled to the space it has: the container's width and
 * `maxHeight`. The fit uses the shape
 * the image has once rotated, so a sideways image fills the space as fully as
 * an upright one and leaves no empty band around it.
 */
export const RotatableImage = ({
  base64,
  alt,
  rotation = 0,
  maxHeight,
  centre,
}: {
  base64: string;
  alt: string;
  rotation?: number;
  /** Height limit in px. */
  maxHeight: number;
  centre?: boolean;
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(
    null
  );

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const src = `data:image/jpg;base64,${base64}`;
  const sideways = rotation % 180 !== 0;
  const availableWidth = width;
  const availableHeight = maxHeight;

  if (!natural || !availableWidth || !availableHeight) {
    return (
      <FitBox ref={boxRef} $centre={centre}>
        <LoadingImage
          src={src}
          alt={alt}
          maxHeight={availableHeight ? `${availableHeight}px` : undefined}
          onLoad={(e) => {
            const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
            if (w && h) setNatural({ w, h });
          }}
        />
      </FitBox>
    );
  }

  const [shownWidth, shownHeight] = sideways
    ? [natural.h, natural.w]
    : [natural.w, natural.h];
  const scale = Math.min(
    availableWidth / shownWidth,
    availableHeight / shownHeight,
    MAX_UPSCALE
  );

  return (
    <FitBox ref={boxRef} $centre={centre}>
      <Stage style={{ width: shownWidth * scale, height: shownHeight * scale }}>
        <StagedImage
          src={src}
          alt={alt}
          style={{
            width: natural.w * scale,
            height: natural.h * scale,
            transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
          }}
        />
      </Stage>
    </FitBox>
  );
};

/** A button wrapping a thumbnail; opens the full-size view. */
export const ThumbButton = styled.button`
  ${({ theme: { colors, radii } }) => `
    display: block;
    width: 100%;
    padding: 0;
    border: none;
    background: none;
    border-radius: ${radii[0]};
    cursor: zoom-in;

    &:hover img, &:focus-visible img {
      border-color: ${colors.blue.primary};
    }
    &:focus-visible {
      outline: none;
    }
  `}
`;


export const ImageViewer = ({
  image,
  rotation,
  onRotate,
  onClose,
}: {
  image?: ViewableImage | null;
  rotation: number;
  onRotate: () => void;
  onClose: () => void;
}) => {
  if (!image?.base64) return null;
  return (
    <Modal isOpen onClose={onClose} width="80vw">
      <Flex
        justifyContent="space-between"
        alignItems="center"
        gap="8"
        mb={2}
        pr={4}
      >
        <Paragraph m="0px" fontWeight="600" color="gray.80">
          {image.label}
        </Paragraph>
        <Flex gap="8">
          <Button
            variant="secondary"
            size="small"
            Icon={FiRotateCw}
            iconSize="14px"
            onClick={onRotate}
          >
            Rotate
          </Button>
          <Button
            variant="secondary"
            size="small"
            Icon={FiDownload}
            iconSize="14px"
            onClick={() =>
              downloadBase64Image(image.base64, image.downloadName)
            }
          >
            Download
          </Button>
        </Flex>
      </Flex>
      {/* Sized to the image, so a wide or rotated image has no empty band. */}
      <RotatableImage
        base64={image.base64}
        alt={image.label}
        rotation={rotation}
        maxHeight={Math.round(window.innerHeight * 0.75)}
        centre
      />
    </Modal>
  );
};
